import Button from '@atoms/Button/Button';
import Tags from '@atoms/Tags/Tags';
import AlgoliaPagination from '@molecules/Pagination/AlgoliaPagination';
import { stripHtml } from '@utils';
import useStore from '@utils/useStore';
import classNames from 'classnames';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import styles from './SearchResults.module.scss';

const TIMEOUT_IN_MS = 650;

export default function SearchResults({ id, isSearchModal }) {
  const { results } = useInstantSearch();
  const {
    store: { searchIndex },
  } = useStore();

  const timeoutId = useRef(null);

  const getSlug = result => {
    switch (result.model) {
      case 'Pagina: Actueel':
        return `/actueel${result.slug}`;
      case 'Pagina: Locatie':
        return `/locaties${result.slug}`;
      case 'Pagina: Verhaal':
        return `/verhalen${result.slug}`;
      case 'Pagina: Evenement':
        return `/agenda${result.slug}`;
      case 'Vacancy':
        if (result.childrenCount === 0)
          return `/vacatures/${result.slug}/${result.id}`;
        return `/vacatures/${result.slug}`;
      case 'Unieke Pagina: Home':
        return '/';
      case 'Unieke Pagina: Matchmaker':
        return '/matchmaker';
      case 'Unieke Pagina: Vacaturemelding':
        return '/vacatures/vacaturemelding';
      case 'Unieke Pagina: Agenda overzicht':
        return '/agenda';
      case 'Unieke Pagina: Locatie overzicht':
        return '/locaties';
      case 'Unieke Pagina: Actueel overzicht':
        return '/actueel';
      case 'Unieke Pagina: Verwijsformulier':
        return '/verwijsformulier';
      case 'Unieke Pagina: Vacature overzicht':
        return '/vacatures';
      case 'Unieke Pagina: Verhalen overzicht':
        return '/verhalen';
      default:
        return result.slug;
    }
  };

  useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      if (window.dataLayer && results.query !== '') {
        // Push the matchmaker answers to the dataLayer
        window.dataLayer.push({
          event: 'search-results',
          searchQuery: results.query,
          searchNbHits: results.nbHits,
        });
      }
    }, TIMEOUT_IN_MS);
  }, [results]);

  return (
    <SearchBoundary>
      <p className={styles.total}>
        {`${results.nbHits} ${
          results.nbHits === 1 ? `resultaat` : `resultaten`
        } gevonden voor "${results.query}"`}
      </p>

      <ul
        id={id}
        className={classNames(styles.results, 'u-list-clean')}
        role="listbox"
        aria-label="Zoekresultaten"
      >
        {results.hits.map(hit => {
          const href = getSlug(hit);

          if (!href) return null;

          const isVacancyPage = hit.model === 'Vacancy';
          const tags = (hit.categories || hit.fieldsOfWork || []).map(
            category => ({ label: category })
          );
          const description = hit.text || hit.intro;
          return (
            <li
              key={hit.objectID}
              role="option"
              className={styles.hitWrapper}
              aria-selected={false}
              id={hit.objectID}
            >
              <div className={styles.hit}>
                {!!tags?.length && <Tags tags={tags} />}
                {isVacancyPage && (
                  <Tags
                    tags={[{ label: 'Vacature', id: `tag-${hit.objectID}` }]}
                  />
                )}
                <Link
                  href={href}
                  className={classNames(styles.hitTitle, 'u-umbrella-link')}
                >
                  {hit.title}
                </Link>

                {description && (
                  <p className={styles.hitDescription}>
                    {stripHtml(description)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {isSearchModal && results.nbPages > 1 && (
        <Button
          href={`${window.location.host}/zoekresultaten?${searchIndex}%5Bquery%5D=${results.query}`}
          label="Bekijk alle resultaten"
          size="small"
          modifierClass="searchResults"
          extraClasses={styles.link}
        />
      )}

      {!isSearchModal && results.nbPages > 1 && <AlgoliaPagination />}
    </SearchBoundary>
  );
}

function NoQuery() {
  return (
    <p className={styles.total}>
      Voer een zoekopdracht in om resultaten te zien
    </p>
  );
}

function NoResults() {
  const { results, setUiState } = useInstantSearch();

  return (
    <>
      <p className={styles.total}>
        Geen resultaten gevonden voor &ldquo;{results.query}&rdquo;
      </p>

      <Button
        onClick={() => {
          setUiState({});
        }}
        label="Zoekopdracht wissen"
        extraClasses="u-mt-2"
      />
    </>
  );
}

function SearchBoundary({ children }) {
  const { results, indexUiState } = useInstantSearch();
  if (!indexUiState.query) {
    // No query so show the no query message
    return <NoQuery />;
  }
  // eslint-disable-next-line no-underscore-dangle
  if (!results.__isArtificial && results.nbHits === 0) {
    // No results so show the no results message
    return <NoResults />;
  }
  // We have results so show them
  return children;
}

SearchBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

SearchResults.propTypes = {
  id: PropTypes.string.isRequired,
  isSearchModal: PropTypes.bool,
};
